"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.opRegisterContextResponseSchema = exports.opRegisterReportHeaderResponseSchema = exports.opRegisterReconciliationResponseSchema = exports.opRegisterTenderMatrixResponseSchema = exports.opRegisterTenderCounterSchema = exports.opRegisterTenderUserSchema = exports.opRegisterTenderBankSchema = exports.opRegisterByUserResponseSchema = exports.opRegisterByUserRowSchema = exports.opRegisterCreditSplitResponseSchema = exports.opRegisterUserSettlementSchema = exports.opRegisterGroupsResponseSchema = exports.opRegisterGroupTotalResponseSchema = exports.opRegisterRefundsResponseSchema = exports.opRegisterRefundRowResponseSchema = exports.opRegisterSessionsResponseSchema = exports.opRegisterSessionRowResponseSchema = exports.opRegisterGroupResponseSchema = exports.opRegisterSummaryResponseSchema = exports.opRegisterPageResponseSchema = exports.opRegisterBillDetailResponseSchema = exports.opRegisterBillDetailLineResponseSchema = exports.opRegisterRowResponseSchema = exports.opRegisterAuditResponseSchema = exports.opRegisterFinanceLinkResponseSchema = exports.opRegisterPaymentSplitResponseSchema = void 0;
/**
 * OP Billing Register — response contracts. ADR-084.
 *
 * EVERY FIGURE HERE IS SERVER-COMPUTED, and the browser renders them without
 * adding, subtracting or totalling any of them (ADR-062 §6A.10). That is why
 * summary cards, grand totals and group subtotals are all their own fields
 * rather than something the grid folds — a footer sum in the client would be a
 * money calculation in the browser, and this is the most tempting place in the
 * product to add one.
 *
 * FINANCIAL vs ORGANISATIONAL, the ADR-065 §1.5 split, holds throughout:
 *
 *   financial attributes  (codes, rates, GST, amounts, classification)
 *       → from the row's OWN snapshot columns. A renamed service or a re-filed
 *         account head must never alter a historical register row.
 *   organisational labels (patient, doctor, department, cashier names)
 *       → from the current master. The register is showing who that person IS
 *         NOW, so today's value is the correct one.
 */
const zod_1 = require("zod");
const op_billing_register_request_1 = require("../request/op-billing-register.request");
/**
 * The per-mode split for one bill.
 *
 * PRO-RATED BY ALLOCATION SHARE, never taken raw from `FinPaymentLine`
 * (ADR-084 §3): one payment can settle several invoices, so the raw line amount
 * belongs to the payment, not to this bill. The parts sum EXACTLY to the bill's
 * allocated amount — a partition, not a recomputation.
 *
 * `otherPaise` is a NAMED bucket for any mode outside the five, for the same
 * reason ADR-083 names `(Unclassified)`: revenue that is dropped stops
 * reconciling, and a total that quietly omits a mode is worse than one that
 * admits it cannot classify it.
 */
exports.opRegisterPaymentSplitResponseSchema = zod_1.z.object({
    cashPaise: zod_1.z.number().int(),
    cardPaise: zod_1.z.number().int(),
    upiPaise: zod_1.z.number().int(),
    chequePaise: zod_1.z.number().int(),
    bankTransferPaise: zod_1.z.number().int(),
    otherPaise: zod_1.z.number().int(),
});
/**
 * The finance link. Present only for callers holding
 * `financial:op-register:view-financial` (journal fields additionally require
 * `view-journal`) — omitted server-side, not hidden client-side (ADR-084 §7).
 *
 * `postingStatus` is NOT_POSTED when the bill has no `sourceEventLogId` at all,
 * which is a real and reportable state rather than an error: a bill posted
 * before the posting engine covered its event type genuinely has no journal.
 */
exports.opRegisterFinanceLinkResponseSchema = zod_1.z.object({
    /** From the line's `revenueLedgerCodeSnapshot` — never joined to a live account. */
    revenueLedgerCode: zod_1.z.string().nullable(),
    revenueLedgerName: zod_1.z.string().nullable(),
    journalNumber: zod_1.z.string().nullable(),
    postingStatus: zod_1.z.enum(['POSTED', 'DRAFT', 'NOT_POSTED']),
    postingDate: zod_1.z.string().nullable(),
    /**
     * The accounting period the entry landed in. That period IS the trial-balance
     * coordinate — there is no separate "TB reference" entity in this schema, and
     * inventing one would be inventing data.
     */
    accountingPeriodId: zod_1.z.string().nullable(),
    accountingPeriodName: zod_1.z.string().nullable(),
});
/** Who touched the bill and when. `postedBy`/`postedAt` are the invoice's own creation — an invoice exists ONLY once posted. */
exports.opRegisterAuditResponseSchema = zod_1.z.object({
    createdBy: zod_1.z.string().nullable(),
    createdByName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    modifiedBy: zod_1.z.string().nullable(),
    modifiedByName: zod_1.z.string().nullable(),
    modifiedAt: zod_1.z.string().nullable(),
    postedBy: zod_1.z.string().nullable(),
    postedByName: zod_1.z.string().nullable(),
    postedAt: zod_1.z.string(),
    /** Always null on a register row — a cancelled bill is fenced out (§1). Present so the shape is stable for a future all-status audit view. */
    cancellationReason: zod_1.z.string().nullable(),
});
/**
 * ONE ROW, serving BOTH grains.
 *
 * One schema rather than two, because a client that had to branch on grain to
 * read a row would branch inconsistently — and the export, the print format and
 * the grid would each get it slightly differently wrong.
 *
 * THE GRAIN CONTRACT, stated exactly:
 *
 *   BILL grain    — one row per bill. Every `bill*` field is populated.
 *                   Line fields (`serviceCode`, `qty`, `unitRate`…) are null;
 *                   `serviceCount` says how many lines the expand will show.
 *
 *   SERVICE grain — one row per invoice line. Every line field is populated.
 *                   Bill-level MONEY appears on the bill's FIRST line only
 *                   (`isBillAnchor: true`) and is null on the rest.
 *
 * That last rule is the whole reason `isBillAnchor` exists. It means a naive
 * `SUM()` over an exported bill-level column yields the correct bill total
 * instead of one multiplied by the number of services — and it is decided ONCE,
 * on the server, rather than three times by three renderers.
 */
exports.opRegisterRowResponseSchema = zod_1.z.object({
    // --- identity -----------------------------------------------------------
    rowKey: zod_1.z.string(),
    invoiceId: zod_1.z.string(),
    lineId: zod_1.z.string().nullable(),
    /** True when bill-level figures on this row are authoritative. Always true in BILL grain. */
    isBillAnchor: zod_1.z.boolean(),
    // --- document -----------------------------------------------------------
    /**
     * THE RECEIPT NUMBER — the number on the paper handed across the counter.
     *
     * The field keeps its name because every export, filter and caller already
     * uses it, but the desk reads it as the RECEIPT number and the register now
     * labels it that way (2026-09-07). On an OP bill the document is both the
     * bill and the receipt, so this is its invoice number; on an IP advance it is
     * the deposit's own receipt number, which comes from the shared OP series.
     */
    billNumber: zod_1.z.string(),
    /**
     * THE BILL THIS MONEY IS AGAINST — a different number from the receipt above
     * whenever the two are different documents.
     *
     * Desk, 2026-09-07: the collection register must carry Receipt No AND Bill
     * Number. On an IP advance they genuinely differ — the clerk hands over
     * receipt `2026000556` for a stay whose bill number is `000078` — and the
     * register had no column for the second, so a deposit could not be traced to
     * its stay from this report at all.
     *
     * On an OP bill the receipt and the bill ARE one document, so this repeats
     * the number above rather than going empty: a blank would read as missing
     * data about a bill that exists.
     *
     * Nullable for an advance whose admission predates the IP bill series.
     */
    linkedBillNumber: zod_1.z.string().nullable(),
    /** `invoiceDate` is a DATE column; the TIME comes from `createdAt`, which for an invoice is the posting instant. */
    billDate: zod_1.z.string(),
    billTime: zod_1.z.string(),
    billStatus: zod_1.z.string(),
    /**
     * IS THE MONEY'S BILL SETTLED — desk, 2026-09-07: "STATUS should show whether
     * bill settlement done or not".
     *
     * Deliberately NOT the same question as `billStatus`, which names the
     * DOCUMENT's state (`PAID`, `ADVANCE`, …). An advance is always "ADVANCE" for
     * as long as it exists, which told the desk nothing about whether the stay it
     * belongs to had been settled — the one thing they are scanning this register
     * for.
     *
     * SETTLED means: an OP invoice with nothing outstanding, or an IP stay
     * carrying a settlement in `SETTLED` state. PENDING is everything else.
     *
     * Null only where neither question applies.
     */
    settlementState: zod_1.z.enum(['SETTLED', 'PENDING']).nullable(),
    refundState: zod_1.z.string(),
    // --- patient (display labels) ------------------------------------------
    /**
     * NULL ON A WALK-IN BILL — a customer who is not a patient. `patientName`
     * still carries who was billed; it is the walk-in's own name in that case.
     */
    patientId: zod_1.z.string().nullable(),
    uhid: zod_1.z.string().nullable(),
    patientName: zod_1.z.string().nullable(),
    /** Whole years at the BILL DATE, not at today — an age that drifts makes a reprint disagree with the original. Null when the patient has no recorded DOB. */
    ageYears: zod_1.z.number().int().nullable(),
    gender: zod_1.z.string().nullable(),
    mobile: zod_1.z.string().nullable(),
    /** True when this bill has no `patientId` — a walk-in, priced against a name rather than a patient record. */
    isWalkIn: zod_1.z.boolean(),
    /**
     * The allotted Private Patient Register number — see
     * `FinInvoice.walkInNumber`. Null on a patient-identified bill (the
     * patient's own UHID is that number there) and on a historical walk-in
     * posted before this series existed.
     */
    walkInNumber: zod_1.z.string().nullable(),
    // --- clinical / organisational -----------------------------------------
    doctorId: zod_1.z.string().nullable(),
    doctorName: zod_1.z.string().nullable(),
    departmentId: zod_1.z.string().nullable(),
    departmentName: zod_1.z.string().nullable(),
    // --- line: ADR-083 classification, all snapshots (SERVICE grain) --------
    serviceDepartment: zod_1.z.string().nullable(),
    accountHeadId: zod_1.z.string().nullable(),
    revenueSubHead: zod_1.z.string().nullable(),
    serviceCode: zod_1.z.string().nullable(),
    serviceNumber: zod_1.z.string().nullable(),
    /**
     * THE ACCOUNT HEAD'S OWN CODE (its legacy INCODE, e.g. `441.00`) — the code a line
     * carries when it was billed against an ACCOUNT HEAD rather than a Service Master
     * row (desk, 2026-09-25: the Detailed Excel "not showing serv code").
     *
     * Roughly two lines in three at this hospital are of that kind — every
     * consultation, tuition, certificate and registration fee — and they have NO
     * `serviceCode`/`serviceNumber` by design (ADR-083: a snapshot rule forbids
     * inventing a service identifier for a line that had no service). Their identity
     * is this. Null only on a bill-grain row and on a line with no account head.
     */
    accountCode: zod_1.z.string().nullable(),
    serviceName: zod_1.z.string().nullable(),
    serviceDescription: zod_1.z.string().nullable(),
    priceVersionId: zod_1.z.string().nullable(),
    /** Populated in BILL grain only — how many lines the expand will show. */
    serviceCount: zod_1.z.number().int().nullable(),
    // --- line money (SERVICE grain) ----------------------------------------
    quantity: zod_1.z.number().nullable(),
    unitRatePaise: zod_1.z.number().int().nullable(),
    lineGrossPaise: zod_1.z.number().int().nullable(),
    lineDiscountPaise: zod_1.z.number().int().nullable(),
    lineDiscountPercent: zod_1.z.number().nullable(),
    lineTaxablePaise: zod_1.z.number().int().nullable(),
    lineGstPercent: zod_1.z.number().nullable(),
    lineGstRateName: zod_1.z.string().nullable(),
    lineGstPaise: zod_1.z.number().int().nullable(),
    lineNetPaise: zod_1.z.number().int().nullable(),
    // --- bill money (null on non-anchor SERVICE rows) ----------------------
    billGrossPaise: zod_1.z.number().int().nullable(),
    billDiscountPaise: zod_1.z.number().int().nullable(),
    billDiscountPercent: zod_1.z.number().nullable(),
    billTaxablePaise: zod_1.z.number().int().nullable(),
    billGstPaise: zod_1.z.number().int().nullable(),
    billRoundOffPaise: zod_1.z.number().int().nullable(),
    /** The payable total: net of discount, inclusive of GST, after round-off. */
    billFinalPaise: zod_1.z.number().int().nullable(),
    billCollectedPaise: zod_1.z.number().int().nullable(),
    billBalancePaise: zod_1.z.number().int().nullable(),
    /** Advance deposit applied against this bill, from the allocation ledger. */
    billAdvanceAdjustedPaise: zod_1.z.number().int().nullable(),
    // --- payment (null on non-anchor SERVICE rows) -------------------------
    paymentSplit: exports.opRegisterPaymentSplitResponseSchema.nullable(),
    /** The single mode when a bill was settled by exactly one; null for split or unsettled. */
    primaryPaymentMode: zod_1.z.string().nullable(),
    payerId: zod_1.z.string().nullable(),
    payerName: zod_1.z.string().nullable(),
    payerClass: op_billing_register_request_1.opRegisterPayerClassSchema.nullable(),
    /** Employee or company when this was a credit bill. Corporate/CGHS/Insurance all read from `payerClass` (§4). */
    creditPartyType: zod_1.z.string().nullable(),
    creditPartyName: zod_1.z.string().nullable(),
    /**
     * THE BANK ACCOUNT(S) THE NON-CASH MONEY LANDED IN — desk, 2026-10-08, for the
     * two Excel exports. Read off `FinPaymentLine.bankAccountId`, which the cashier
     * chose at entry; null for a cash-only bill (cash lands in a drawer, not an
     * account) and for rows taken before the column existed.
     *
     * ONE CELL EACH, "; "-joined when a bill was paid across two accounts (UPI to
     * one, card to another), in the SAME order in both fields so the n-th name and
     * the n-th number are the same account. Descriptive, not money: carried on
     * EVERY row of a bill, not only its anchor.
     *
     * Optional so a row built without a bank lookup still type-checks.
     */
    bankName: zod_1.z.string().nullable().optional(),
    bankAccountNumber: zod_1.z.string().nullable().optional(),
    // --- counter ------------------------------------------------------------
    cashierUserId: zod_1.z.string().nullable(),
    cashierName: zod_1.z.string().nullable(),
    counterId: zod_1.z.string().nullable(),
    counterName: zod_1.z.string().nullable(),
    cashSessionId: zod_1.z.string().nullable(),
    // --- finance + audit ----------------------------------------------------
    finance: exports.opRegisterFinanceLinkResponseSchema.nullable(),
    audit: exports.opRegisterAuditResponseSchema.nullable(),
    printCount: zod_1.z.number().int(),
});
/** One service on the expanded bill. Read from the line's OWN snapshots — never joined to ChargeMaster. */
exports.opRegisterBillDetailLineResponseSchema = zod_1.z.object({
    lineId: zod_1.z.string(),
    serviceCode: zod_1.z.string().nullable(),
    serviceNumber: zod_1.z.string().nullable(),
    serviceName: zod_1.z.string().nullable(),
    description: zod_1.z.string(),
    departmentName: zod_1.z.string().nullable(),
    accountHeadId: zod_1.z.string().nullable(),
    revenueSubHead: zod_1.z.string().nullable(),
    revenueLedgerCode: zod_1.z.string().nullable(),
    revenueLedgerName: zod_1.z.string().nullable(),
    priceVersionId: zod_1.z.string().nullable(),
    gstPercent: zod_1.z.number().nullable(),
    gstRateName: zod_1.z.string().nullable(),
    quantity: zod_1.z.number().nullable(),
    unitRatePaise: zod_1.z.number().int().nullable(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    taxablePaise: zod_1.z.number().int(),
    taxPaise: zod_1.z.number().int(),
    netPaise: zod_1.z.number().int(),
    hsnSacCode: zod_1.z.string().nullable(),
    /** Which resolver rule produced this rate (ADR-045). Null on a line that bypassed ECM — that NULL is the detection mechanism and is never filled in with a guess. */
    rateSource: zod_1.z.string().nullable(),
});
exports.opRegisterBillDetailResponseSchema = zod_1.z.object({
    bill: exports.opRegisterRowResponseSchema,
    lines: zod_1.z.array(exports.opRegisterBillDetailLineResponseSchema),
    finance: exports.opRegisterFinanceLinkResponseSchema.nullable(),
    audit: exports.opRegisterAuditResponseSchema.nullable(),
    /** Every payment that touched this bill, with the allocation share the split was pro-rated by. */
    payments: zod_1.z.array(zod_1.z.object({
        paymentId: zod_1.z.string(),
        paymentNumber: zod_1.z.string(),
        paymentDate: zod_1.z.string(),
        receiptNumber: zod_1.z.string().nullable(),
        allocatedPaise: zod_1.z.number().int(),
        modes: zod_1.z.array(zod_1.z.object({ mode: zod_1.z.string(), amountPaise: zod_1.z.number().int() })),
    })),
    /** POSTED journal legs behind this bill. Requires `view-journal`; null otherwise. */
    journal: zod_1.z
        .object({
        entryNumber: zod_1.z.string(),
        entryDate: zod_1.z.string(),
        status: zod_1.z.string(),
        narration: zod_1.z.string().nullable(),
        lines: zod_1.z.array(zod_1.z.object({
            accountCode: zod_1.z.string(),
            accountName: zod_1.z.string(),
            drOrCr: zod_1.z.string(),
            amountPaise: zod_1.z.number().int(),
        })),
    })
        .nullable(),
});
exports.opRegisterPageResponseSchema = zod_1.z.object({
    grain: op_billing_register_request_1.opRegisterGrainSchema,
    data: zod_1.z.array(exports.opRegisterRowResponseSchema),
    nextCursor: zod_1.z.string().nullable(),
    hasMore: zod_1.z.boolean(),
    /**
     * Total rows matching the filter, for the header's "Total Records".
     *
     * A COUNT, not a page length. It is the one number an operator uses to decide
     * whether the filter is right before exporting, so it must describe the whole
     * set rather than what happens to be loaded.
     */
    totalRecords: zod_1.z.number().int(),
});
/**
 * Summary cards + grand totals. Computed over the WHOLE filtered set by
 * aggregate query, never by folding fetched rows — the set is specified to
 * reach 100,000+ bills and the cards must not depend on how much of it is on
 * screen.
 */
exports.opRegisterSummaryResponseSchema = zod_1.z.object({
    totalBills: zod_1.z.number().int(),
    /**
     * IP ADVANCE RECEIPTS IN THE SAME WINDOW — deliberately NOT folded into
     * `totalBills`, and now stated so a reader can add them.
     *
     * An advance is not a bill: it bills nothing, renders no service, and is
     * excluded from `totalBills`, `netRevenuePaise` and the average and extremes
     * for the reason those all describe one population. Its MONEY is in
     * `collectedPaise` and `paymentSplit`, because the cash did arrive.
     *
     * WHY IT HAD TO BE EXPOSED (desk, 2026-09-08: "ip advance is not totalling
     * why?"). The collection report foots its table with "Total · all N rows"
     * taken from `totalBills` — so on a day with four advances the line claimed
     * to cover fewer rows than were visible above it, and the advance cash in the
     * tender columns looked as though nothing had counted it. It had; the LABEL
     * was describing a smaller set than the table.
     */
    advanceCount: zod_1.z.number().int(),
    totalServices: zod_1.z.number().int(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    taxablePaise: zod_1.z.number().int(),
    gstPaise: zod_1.z.number().int(),
    roundOffPaise: zod_1.z.number().int(),
    /** The payable total across the set. Net of discount, inclusive of GST, after round-off. */
    netRevenuePaise: zod_1.z.number().int(),
    collectedPaise: zod_1.z.number().int(),
    outstandingPaise: zod_1.z.number().int(),
    paymentSplit: exports.opRegisterPaymentSplitResponseSchema,
    /**
     * COLLECTION BY THE ACCOUNT IT LANDED IN (desk, 2026-09-08: "bank wise list
     * amount"). The operator chooses the bank when entering the payment, so this
     * reports a recorded fact rather than inferring one.
     *
     * CASH IS NOT IN THIS LIST — it reaches a drawer, not an account, and it is
     * already its own line on the summary. Including it under "(no bank)" would
     * make the list look like it double-counts the collection total. Apportioned
     * from the SAME share vector as `paymentSplit`, so the two cannot disagree.
     */
    bankSplit: zod_1.z.array(zod_1.z.object({
        bankAccountId: zod_1.z.string(),
        bankName: zod_1.z.string(),
        accountNumber: zod_1.z.string().nullable(),
        amountPaise: zod_1.z.number().int(),
    })),
    /**
     * WHAT WAS BILLED AND NOT COLLECTED, by who is expected to settle it — the
     * "other mode" block.
     *
     * This system has NO credit payment mode: all nine configured modes are money
     * that arrived. Credit is therefore OUTSTANDING, split by the party that owes
     * it — a named payer (company) or a patient flagged as staff. It is NOT the
     * staff concession, which is a discount and already sits in `discountPaise`.
     */
    creditSplit: zod_1.z.object({
        companyPaise: zod_1.z.number().int(),
        staffPaise: zod_1.z.number().int(),
    }),
    /** By payer class — the Corporate / CGHS / Insurance cards (§4). */
    payerClassSplit: zod_1.z.array(zod_1.z.object({ payerClass: zod_1.z.string(), bills: zod_1.z.number().int(), netPaise: zod_1.z.number().int() })),
    /** Null when the set is empty — a zero average over zero bills is a lie, not a statistic. */
    averageBillPaise: zod_1.z.number().int().nullable(),
    highestBillPaise: zod_1.z.number().int().nullable(),
    lowestBillPaise: zod_1.z.number().int().nullable(),
});
/** One group's subtotal. Server-computed for the same reason every other total is. */
exports.opRegisterGroupResponseSchema = zod_1.z.object({
    groupKey: zod_1.z.string(),
    groupLabel: zod_1.z.string(),
    bills: zod_1.z.number().int(),
    services: zod_1.z.number().int(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    taxablePaise: zod_1.z.number().int(),
    gstPaise: zod_1.z.number().int(),
    netPaise: zod_1.z.number().int(),
    collectedPaise: zod_1.z.number().int(),
    /**
     * COLLECTED, SPLIT TWO WAYS (desk, 2026-09-27: "remove GST... need cash and
     * bank(bank includes all type of card, cheque, upi, transfer)"). `cashPaise`
     * is the same field name `OpRegisterPaymentSplitDto` uses; `bankPaise` is
     * `nonCashPaise()` from `op-register.payment-split.ts` — "every tender that
     * is not cash" — pre-summed there for exactly this reason, so this response
     * never asks a screen to add card+UPI+cheque+bank-transfer+other itself.
     * The two always sum to `collectedPaise`.
     */
    cashPaise: zod_1.z.number().int(),
    bankPaise: zod_1.z.number().int(),
    /**
     * This group's share of the whole filtered set's collections, in BASIS POINTS
     * (10000 = 100.00%).
     *
     * SERVER-COMPUTED, and an integer. The browser is forbidden from dividing one
     * money figure by another (ADR-062 §6B), and a float would reintroduce the
     * rounding drift that basis points exist to avoid. Null when the set
     * collected nothing — a share of zero is undefined, not 0%.
     */
    shareOfCollectedBps: zod_1.z.number().int().nullable(),
});
/* -------------------------------------------------------------------------
 * SESSION REPORT — one row per cashier shift.
 *
 * THE MONEY COMES FROM TWO PLACES AND THE ROW SAYS WHICH.
 *
 * An OPEN drawer's expected cash is computed live from the canonical engine and
 * is `isProvisional: true`. A CLOSED drawer's comes from the frozen settlement
 * snapshot and is `isProvisional: false`. Counted cash and variance are NULL
 * for an open drawer — they do not exist until someone counts, and a zero would
 * assert the drawer was counted and balanced.
 * ------------------------------------------------------------------------- */
exports.opRegisterSessionRowResponseSchema = zod_1.z.object({
    sessionId: zod_1.z.string(),
    businessDate: zod_1.z.string(),
    status: zod_1.z.string(),
    cashierUserId: zod_1.z.string(),
    cashierName: zod_1.z.string().nullable(),
    counterId: zod_1.z.string(),
    counterName: zod_1.z.string().nullable(),
    openedAt: zod_1.z.string(),
    closedAt: zod_1.z.string().nullable(),
    openingFloatPaise: zod_1.z.number().int(),
    /** TRUE while the drawer is open: expected cash is a live sum, not a frozen one. */
    isProvisional: zod_1.z.boolean(),
    expectedCashPaise: zod_1.z.number().int(),
    /** NULL until counted. Never 0. */
    countedCashPaise: zod_1.z.number().int().nullable(),
    variancePaise: zod_1.z.number().int().nullable(),
    cashCollectionsPaise: zod_1.z.number().int().nullable(),
    /** Card / UPI / cheque / bank — context for acquirer reconciliation, never part of expected cash. */
    nonCashTotals: zod_1.z.record(zod_1.z.number().int()).nullable(),
    billCount: zod_1.z.number().int().nullable(),
    settlementId: zod_1.z.string().nullable(),
    settlementDecision: zod_1.z.string().nullable(),
});
exports.opRegisterSessionsResponseSchema = zod_1.z.object({ rows: zod_1.z.array(exports.opRegisterSessionRowResponseSchema) });
/* -------------------------------------------------------------------------
 * REFUND REPORT.
 *
 * REQUESTED BY and PROCESSED BY are separate people and separate columns, the
 * same distinction the collection report draws between a bill's creator and its
 * payment's collector. `processedByName` is null for refunds made before
 * `fin_payments.collected_by` existed and renders as "not recorded" — it is
 * never filled in from the requester.
 * ------------------------------------------------------------------------- */
exports.opRegisterRefundRowResponseSchema = zod_1.z.object({
    refundId: zod_1.z.string(),
    refundNumber: zod_1.z.string(),
    refundedAt: zod_1.z.string(),
    receiptNumber: zod_1.z.string(),
    originalBillNumber: zod_1.z.string().nullable(),
    uhid: zod_1.z.string().nullable(),
    patientName: zod_1.z.string().nullable(),
    amountPaise: zod_1.z.number().int(),
    refundMode: zod_1.z.string().nullable(),
    requestedByName: zod_1.z.string().nullable(),
    processedByName: zod_1.z.string().nullable(),
    counterId: zod_1.z.string().nullable(),
    counterName: zod_1.z.string().nullable(),
    cashSessionId: zod_1.z.string().nullable(),
    reason: zod_1.z.string().nullable(),
});
exports.opRegisterRefundsResponseSchema = zod_1.z.object({ rows: zod_1.z.array(exports.opRegisterRefundRowResponseSchema) });
/**
 * The set's grand total — SERVER-SUMMED, over exactly the `groups` rows
 * returned alongside it, never a wider set. That matters beyond convenience:
 * when a row has been withheld from `groups` (a caller without
 * `FINANCIAL_OP_REGISTER_VIEW_MAIN_COUNTER` never receives the Main Counter's
 * row), this total must NOT include it either — a total that quietly counted
 * a row the caller cannot see would let it be recovered by subtraction
 * (visible rows' own sum vs. this total), which is exactly the leak hiding
 * the row exists to prevent.
 */
exports.opRegisterGroupTotalResponseSchema = zod_1.z.object({
    bills: zod_1.z.number().int(),
    services: zod_1.z.number().int(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    taxablePaise: zod_1.z.number().int(),
    gstPaise: zod_1.z.number().int(),
    netPaise: zod_1.z.number().int(),
    collectedPaise: zod_1.z.number().int(),
    /** Summed across `groups`, exactly as every other total field here is — see that array's own note. */
    cashPaise: zod_1.z.number().int(),
    bankPaise: zod_1.z.number().int(),
});
exports.opRegisterGroupsResponseSchema = zod_1.z.object({
    groupBy: zod_1.z.string(),
    groups: zod_1.z.array(exports.opRegisterGroupResponseSchema),
    total: exports.opRegisterGroupTotalResponseSchema,
});
/* ------------------------------------------------------------------------- *
 * BY USER — the login-wise report.
 *
 * Two halves of one operator's day, side by side, because they answer two
 * different questions and the desk asks both at once:
 *
 *   BILLED     what this login raised — bills, gross, discount, GST, net —
 *              from the same fenced, posted-only invoice set every other
 *              register figure comes from. `cashierUserId` is the axis, the
 *              same column `groupBy=CASHIER` uses.
 *   COLLECTED  what actually came in under it, pro-rated per payment mode by
 *              allocation share (ADR-084 §3). Collected is NOT billed: a
 *              credit bill raises revenue and collects nothing, and a balance
 *              settled today against last week's bill collects money this
 *              report must not attribute to today's billing.
 *   SETTLED    the CASH half only — this login's cash sessions for the same
 *              business dates: opening float, what the drawer was expected to
 *              hold, what was counted, and the variance. Card and UPI are
 *              collected but never settled at a drawer, so a settlement block
 *              that included them would invite a reconciliation that can never
 *              balance.
 *
 * WHY SETTLEMENT IS A SEPARATE BLOCK RATHER THAN MORE COLUMNS. Its grain is the
 * SESSION, not the bill: one session spans many bills, a login may open several
 * in a day, and a session's business date is resolved once at open and never
 * recomputed — so a 20:00-08:00 shift settles on ONE date while its bills carry
 * two. Folding the two grains into one row would produce a variance that looks
 * like a shortfall and is arithmetic.
 * ------------------------------------------------------------------------- */
/** One login's cash settlement over the range. Null when they opened no session. */
exports.opRegisterUserSettlementSchema = zod_1.z.object({
    sessions: zod_1.z.number().int(),
    openSessions: zod_1.z.number().int(),
    openingFloatPaise: zod_1.z.number().int(),
    /** Sum over CLOSED sessions only — an open drawer has no expected close yet. */
    expectedClosingPaise: zod_1.z.number().int(),
    countedClosingPaise: zod_1.z.number().int(),
    /**
     * Counted minus expected, summed. NEGATIVE is a shortfall.
     *
     * Summed from each session's own stored `variance` rather than recomputed
     * from the two totals above: the session is where the count was made and
     * approved, and a second derivation here would disagree with the settlement
     * screen the moment a session is closed with an override.
     */
    variancePaise: zod_1.z.number().int(),
});
/**
 * WHAT WAS NOT COLLECTED, and who owes it — the second half of a shift report.
 *
 * A credit bill takes NO money at the counter: `postOpBillRequest` refuses a
 * `creditParty` together with `payments`, so every rupee of it is absent from
 * the payment split above by construction. A report that showed only the split
 * would say a cashier who raised ₹40,000 of corporate credit collected nothing,
 * which is true and useless — the question an auditor asks is "and where did
 * the rest go".
 *
 * SPLIT BY PARTY TYPE because the two are different receivables with different
 * recovery paths: an employee entitlement is settled against payroll, a company
 * bill against a corporate invoice. Summing them would produce a number nobody
 * can act on.
 */
exports.opRegisterCreditSplitResponseSchema = zod_1.z.object({
    employeePaise: zod_1.z.number().int(),
    employeeBills: zod_1.z.number().int(),
    companyPaise: zod_1.z.number().int(),
    companyBills: zod_1.z.number().int(),
    /**
     * Employee + company, SUMMED SERVER-SIDE.
     *
     * Present because ADR-062 §6A.10 forbids the browser adding money fields —
     * and the fitness test enforces it, which is how the first version of the
     * Query tab was caught doing exactly that. A total the screen needs is a
     * total the server owes it.
     */
    totalPaise: zod_1.z.number().int(),
});
exports.opRegisterByUserRowSchema = zod_1.z.object({
    /** Null for bills posted with no cashier recorded — reported, never dropped. */
    userId: zod_1.z.string().nullable(),
    userName: zod_1.z.string(),
    bills: zod_1.z.number().int(),
    grossPaise: zod_1.z.number().int(),
    discountPaise: zod_1.z.number().int(),
    gstPaise: zod_1.z.number().int(),
    netPaise: zod_1.z.number().int(),
    collectedPaise: zod_1.z.number().int(),
    outstandingPaise: zod_1.z.number().int(),
    paymentSplit: exports.opRegisterPaymentSplitResponseSchema,
    /** The other half of the money: what was billed to credit and to whom. */
    creditSplit: exports.opRegisterCreditSplitResponseSchema,
    settlement: exports.opRegisterUserSettlementSchema.nullable(),
});
exports.opRegisterByUserResponseSchema = zod_1.z.object({
    rows: zod_1.z.array(exports.opRegisterByUserRowSchema),
    /** The same shape as a row, over every login — so the footer and the rows cannot disagree. */
    totals: exports.opRegisterByUserRowSchema,
});
/**
 * THE TENDER MATRIX — counter, then user, across every payment method, with the
 * bank account each non-cash tender landed in (desk, 2026-09-26: the Excel sheet
 * the accounts team keeps by hand — a row per counter, a row per user under it,
 * a column per cash / UPI / card / cheque, and "all bank number info").
 *
 * EVERY FIGURE IS SERVER-SUMMED, INCLUDING THE TOTALS. The browser may not add
 * money (ADR-062 §6A.10), so a row's total, a counter's subtotal, the grand
 * total and each bank's total all arrive as fields.
 *
 * THE BANK COLUMNS EXPLAIN THE NON-CASH COLUMNS; THEY DO NOT ADD TO THEM. A
 * card payment entered without a bank (rows written before the operator was
 * asked) is reported under a named "Bank not recorded" entry — `bankAccountId`
 * null — so a user's bank lines always sum to their non-cash money and a gap
 * is visible instead of silently absent. Cash reaches a drawer, not an
 * account, and never appears in a bank list.
 */
exports.opRegisterTenderBankSchema = zod_1.z.object({
    /** Null = the tender carried no bank. Named on screen, never dropped. */
    bankAccountId: zod_1.z.string().nullable(),
    bankName: zod_1.z.string(),
    accountNumber: zod_1.z.string().nullable(),
    ifscCode: zod_1.z.string().nullable(),
    /** This bank's money by payment method. `cashPaise` is always 0 here. */
    byMode: exports.opRegisterPaymentSplitResponseSchema,
    totalPaise: zod_1.z.number().int(),
});
const opRegisterTenderCellsSchema = zod_1.z.object({
    split: exports.opRegisterPaymentSplitResponseSchema,
    /** Every method summed, server-side. */
    totalPaise: zod_1.z.number().int(),
    /** Everything but cash — what the bank lines below explain. Server-summed for the same reason. */
    nonCashPaise: zod_1.z.number().int(),
    banks: zod_1.z.array(exports.opRegisterTenderBankSchema),
});
exports.opRegisterTenderUserSchema = opRegisterTenderCellsSchema.extend({
    /** Null for money with no recorded collector — reported, never dropped. */
    userId: zod_1.z.string().nullable(),
    userName: zod_1.z.string(),
});
exports.opRegisterTenderCounterSchema = opRegisterTenderCellsSchema.extend({
    /** Null for money taken at no recorded counter — reported, never dropped. */
    counterId: zod_1.z.string().nullable(),
    counterName: zod_1.z.string(),
    users: zod_1.z.array(exports.opRegisterTenderUserSchema),
});
exports.opRegisterTenderMatrixResponseSchema = zod_1.z.object({
    counters: zod_1.z.array(exports.opRegisterTenderCounterSchema),
    /** Over exactly the counters returned — a hidden Main Counter is not recoverable by subtraction. */
    totals: opRegisterTenderCellsSchema,
});
/**
 * The reconciliation banner. ADR-084 §6.
 *
 * The two sides are produced INDEPENDENTLY — one from `fin_invoice_lines`, one
 * from `fin_journal_lines` — so agreement is evidence rather than a tautology.
 * The difference is its own column and is never hidden behind a tolerance: a
 * compensating pair of errors that netted to zero would otherwise pass.
 */
exports.opRegisterReconciliationResponseSchema = zod_1.z.object({
    reconciled: zod_1.z.boolean(),
    /** Sum of (net − tax) over the filtered lines. Net of GST: tax credits the tax-payable account, never a revenue ledger. */
    operationalNetPaise: zod_1.z.number().int(),
    /** Signed POSTED credits to INCOME accounts, for the SAME invoice population. */
    ledgerCreditPaise: zod_1.z.number().int(),
    differencePaise: zod_1.z.number().int(),
    /** Bills in the filtered set that reached no journal entry at all. The most common real cause of a difference. */
    unpostedBills: zod_1.z.number().int(),
    /** Lines carrying no account head — reported, never dropped, exactly as ADR-083 requires. */
    unclassifiedNetPaise: zod_1.z.number().int(),
    perLedger: zod_1.z.array(zod_1.z.object({
        revenueLedgerCode: zod_1.z.string(),
        revenueLedgerName: zod_1.z.string(),
        operationalNetPaise: zod_1.z.number().int(),
        ledgerCreditPaise: zod_1.z.number().int(),
        differencePaise: zod_1.z.number().int(),
        reconciled: zod_1.z.boolean(),
    })),
});
/**
 * The report header. Assembled server-side so the printed document and the
 * screen cannot disagree about who ran what, when, and under which filters —
 * a header rebuilt in the browser is a header that drifts.
 */
exports.opRegisterReportHeaderResponseSchema = zod_1.z.object({
    hospitalName: zod_1.z.string(),
    hospitalAddress: zod_1.z.string().nullable(),
    hospitalGstNumber: zod_1.z.string().nullable(),
    hospitalLogoUrl: zod_1.z.string().nullable(),
    reportName: zod_1.z.literal('OP BILLING REGISTER'),
    generatedAt: zod_1.z.string(),
    generatedBy: zod_1.z.string(),
    /** Human-readable, already resolved from ids to names — the print format must never show a raw UUID. */
    filtersApplied: zod_1.z.array(zod_1.z.object({ label: zod_1.z.string(), value: zod_1.z.string() })),
    totalRecords: zod_1.z.number().int(),
});
/** Everything the print format and the page shell need, in one call. */
exports.opRegisterContextResponseSchema = zod_1.z.object({
    header: exports.opRegisterReportHeaderResponseSchema,
    /**
     * What this caller may actually do. Sent rather than inferred from the
     * permission list, so the UI and the router agree by construction.
     */
    capabilities: zod_1.z.object({
        canView: zod_1.z.boolean(),
        canExport: zod_1.z.boolean(),
        canPrint: zod_1.z.boolean(),
        canViewFinancial: zod_1.z.boolean(),
        canViewJournal: zod_1.z.boolean(),
    }),
    /**
     * Fields with no reachable data on an OP bill (ADR-084 §5), each with its
     * reason. Declared and disabled rather than blank or omitted: a blank column
     * is an invisible gap, and an omitted one is a gap nobody will ever close.
     */
    unavailableFields: zod_1.z.array(zod_1.z.object({ field: zod_1.z.string(), reason: zod_1.z.string() })),
});
//# sourceMappingURL=op-billing-register.response.js.map